/* eslint-disable no-console */

import PropTypes from 'prop-types'

import {useState, useEffect} from 'react'
import {connect} from 'react-redux'
import {readEndpoint} from 'redux-json-api'

import PdfViewer from './PdfViewer'

const PdfViewerController = ({
  isModalOpen,
  onCloseModal,
  onDownloadClick,
  pdfName,
  pdfUrl,
  readEndpoint: readEndpointProp,
  uploadId
}) => {
  const [error, setError] = useState(null)
  const [file, setFile] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isModalOpen) return

    const abortController = new AbortController()

    const handleError = err => {
      if (err.name === 'AbortError') return

      console.error('Error fetching PDF:', err)

      let errorMessage = 'Unable to load PDF. Please try again or contact support if the issue persists.'

      if (err instanceof TypeError && !navigator.onLine) {
        errorMessage = 'Network error: Unable to fetch PDF. Please check your internet connection.'
      } else if (err.statusCode) {
        if (err.statusCode >= 500)
          errorMessage = `Server error: The server encountered an error (${err.statusCode}). Please try again later.`
        else if (err.statusCode === 404)
          errorMessage = 'PDF not found. The requested file may have been moved or deleted.'
        else if (err.statusCode === 403)
          errorMessage = 'Access denied. You do not have permission to view this PDF.'
        else
          errorMessage = `Unable to load PDF. Error code: ${err.statusCode}`
      } else if (err.name === 'FileReaderError') {
        errorMessage = 'File read error: Unable to process the PDF file.'
      }

      setError(errorMessage)
      setIsLoading(false)
    }

    const handleSuccess = fileData => {
      setError(null)
      setFile(fileData)
      setIsLoading(false)
    }

    if (pdfUrl) {
      fetch(pdfUrl, {signal: abortController.signal})
        .then(response => {
          if (!response.ok) {
            const fileError = new Error(`HTTP error! status: ${response.status}`)

            fileError.statusCode = response.status

            throw fileError
          }

          return response.blob()
        })
        .then(blob => {
          const fileReader = new FileReader()
          fileReader.onloadend = () => {
            if (abortController.signal.aborted) return

            if (typeof fileReader.result === 'string' && fileReader.result.startsWith('data:')) {
              const base64Data = fileReader.result.split(',')[1]

              handleSuccess(base64Data)
            } else {
              const fileError = new Error('Unexpected file format or empty result')
              fileError.name = 'FileReaderError'
              handleError(fileError)
            }
          }
          fileReader.onerror = () => {
            const fileError = new Error('Failed to read file')
            fileError.name = 'FileReaderError'
            handleError(fileError)
          }
          fileReader.readAsDataURL(blob)
        })
        .catch(handleError)
    } else if (uploadId) {
      readEndpointProp(`uploads/${uploadId}?download=true`)
        .then(response => {
          if (abortController.signal.aborted) return

          handleSuccess(response.body.data.attributes['decrypted-file'])
        })
        .catch(handleError)
    }

    return () => {
      abortController.abort()
    }
  }, [isModalOpen, pdfUrl, readEndpointProp, uploadId])

  return (
    <PdfViewer
      error={error}
      file={file}
      isModalOpen={isModalOpen}
      isLoading={isLoading}
      onDownloadClick={onDownloadClick}
      onCloseModal={onCloseModal}
      pdfName={pdfName}
    />
  )
}

PdfViewerController.propTypes = {
  isModalOpen: PropTypes.bool,
  onDownloadClick: PropTypes.func.isRequired,
  pdfUrl: PropTypes.string,
  pdfName: PropTypes.string,
  readEndpoint: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func,
  uploadId: PropTypes.string
}

export default connect(null, {readEndpoint})(PdfViewerController)
